import React, { useEffect, useState } from 'react';
import {
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonButton,
  IonSkeletonText,
  IonChip,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonContent,
  IonGrid,
  IonRow,
  IonCol,
  IonPopover,
  IonInput,
} from '@ionic/react';
import {
  linkOutline,
  documentOutline,
  folderOutline,
  timeOutline,
  eyeOutline,
  downloadOutline,
  add,
  shareOutline,
  copyOutline,
  qrCodeOutline,
  checkmarkOutline,
  closeOutline,
} from 'ionicons/icons';
import QRCode from 'qrcode';
import { LinkModel } from '../../../types/link';
import styles from '../Dashboard.module.css';
import { LinksService, CategoriesService } from '../../../api';

interface LinkListProps {
  onLinkSelect?: (link: LinkModel) => void;
  onLinkCreate?: () => void;
}

export const LinkList: React.FC<LinkListProps> = ({ onLinkSelect, onLinkCreate }) => {
  const [links, setLinks] = useState<LinkModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [sharePopoverState, setSharePopoverState] = useState<{ isOpen: boolean; link?: LinkModel }>({ isOpen: false });
  const [qrPopoverState, setQrPopoverState] = useState<{ isOpen: boolean; link?: LinkModel }>({ isOpen: false });
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    fetchLinks();
    fetchCategories();
  }, [searchTerm, selectedCategory]);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const data = await LinksService.getLinks({
        searchTerm: searchTerm || undefined,
        categoryId: selectedCategory || undefined
      });
      setLinks(data);
    } catch (error) {
      console.error('Error fetching links:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await CategoriesService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // const handleDelete = async (linkId: string) => {
  //   if (!confirm('Are you sure you want to delete this link?')) return;

  //   try {
  //     await LinksService.deleteLink(linkId);
  //     setLinks(links.filter(link => link.id !== linkId));
  //   } catch (error) {
  //     console.error('Error deleting link:', error);
  //   }
  // };

  const getLinkIcon = (type: string) => {
    switch (type) {
      case 'Redirect':
        return linkOutline;
      case 'File':
        return documentOutline;
      case 'Collection':
        return folderOutline;
      default:
        return linkOutline;
    }
  };

  const getViewerUrl = (linkId: string) => {
    return `${window.location.origin}/viewer/${linkId}`;
  };

  const handleCopyLink = async (linkId: string) => {
    const url = getViewerUrl(linkId);
    try {
      await navigator.clipboard.writeText(url);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async (linkId: string, name: string) => {
    const url = getViewerUrl(linkId);
    if (navigator.share) {
      try {
        await navigator.share({
          title: name,
          url: url
        });
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      handleCopyLink(linkId);
    }
  };

  const generateQRCode = async (linkId: string) => {
    const url = getViewerUrl(linkId);
    const canvas = document.getElementById('qr-code') as HTMLCanvasElement;
    if (canvas) {
      try {
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
      } catch (err) {
        console.error('Error generating QR code:', err);
      }
    }
  };

  if (loading) {
    return (
      <IonContent>
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonSkeletonText animated style={{ width: '60%' }} />
              <IonSkeletonText animated style={{ width: '80%' }} />
              <IonSkeletonText animated style={{ width: '40%' }} />
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    );
  }

  return (
    <div className="ion-content-wrapper">
      <div className={styles.card} style={{ borderRadius: '0' }}>
        <div className={styles.cardHeaderLight}>
          <IonGrid>
            <IonRow className="ion-align-items-center">
              <IonCol size="12" sizeMd="6">
                <IonSearchbar
                  value={searchTerm}
                  onIonChange={e => setSearchTerm(e.detail.value!)}
                  placeholder="Search links..."
                  className="custom-searchbar"
                  style={{
                    '--background': '#f4f5f8',
                    '--border-radius': '8px',
                    '--color': '#1f1f1f',
                    '--placeholder-color': '#92949c'
                  }}
                />
              </IonCol>
              <IonCol size="12" sizeMd="4">
                <IonSelect
                  value={selectedCategory}
                  onIonChange={e => setSelectedCategory(e.detail.value)}
                  placeholder="Filter by category"
                  interface="popover"
                  justify="space-between"
                  labelPlacement="floating" 
                  fill="solid"
                  style={{
                    '--background': '#f4f5f8',
                    '--border-radius': '8px',
                    '--color': '#1f1f1f',
                    '--placeholder-color': '#666666',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    backgroundColor: '#f4f5f8',
                    color: '#1f1f1f',
                    borderRadius: '8px',
                    height: '40px',
                  }}
                >
                  <IonSelectOption value={null}>All Categories</IonSelectOption>
                  {categories.map(category => (
                    <IonSelectOption key={category.id} value={category.id}>
                      {category.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonCol>
              <IonCol size="12" sizeMd="2" className="ion-padding-vertical">
                <IonButton 
                  expand="block" 
                  onClick={onLinkCreate} 
                  color="secondary"
                  fill="solid"
                  className="ion-margin-vertical"
                >
                  <IonIcon slot="start" icon={add} />
                  New Link
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>

        <div className={styles.cardContent}>
          <IonList>
            {links.map(link => (
              <IonItem
                key={link.id}
                button
                onClick={() => onLinkSelect?.(link)}
                className={link.archived ? styles.archived : ''}
              >
                <IonIcon slot="start" icon={getLinkIcon(link.type)} />
                <IonLabel>
                  <h2>{link.name}</h2>
                  <p>{link.description}</p>
                  <div className={styles.chipContainer}>
                    {link.tags && Object.entries(link.tags).map(([key, value]) => (
                      <IonChip key={key}>
                        {value}
                      </IonChip>
                    ))}
                  </div>
                  <div className={styles.linkStats}>
                    <div className={styles.statItem}>
                      <IonIcon icon={timeOutline} />
                      {new Date(link.createdAt).toLocaleDateString()}
                    </div>
                    <div className={styles.statItem}>
                      <IonIcon icon={eyeOutline} />
                      {link.viewCount} views
                    </div>
                    {link.type === 'File' && (
                      <div className={styles.statItem}>
                        <IonIcon icon={downloadOutline} />
                        {link.downloads || 0} downloads
                      </div>
                    )}
                  </div>
                </IonLabel>
                <IonButton
                  fill="clear"
                  size="small"
                  slot="end"
                  className="ion-no-padding ion-margin-start"
                  style={{
                    minWidth: '80px',
                    marginLeft: '16px'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSharePopoverState({ isOpen: true, link });
                  }}
                >
                  <IonIcon slot="start" icon={shareOutline} />
                  Share
                </IonButton>
              </IonItem>
            ))}
          </IonList>
        </div>
      </div>

      <IonPopover
        isOpen={sharePopoverState.isOpen}
        onDidDismiss={() => setSharePopoverState({ isOpen: false })}
        className={styles.sharePopoverContainer}
        showBackdrop={true}
        backdropDismiss={true}
        mode="ios"
      >
        <div className={styles.sharePopover}>
          <div className={styles.modalHeader}>
            <h3>Share Link</h3>
            <IonButton
              fill="clear"
              className={styles.closeButton}
              onClick={() => setSharePopoverState({ isOpen: false })}
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </div>
          <IonInput
            value={sharePopoverState.link ? getViewerUrl(sharePopoverState.link.id) : ''}
            readonly
            fill="solid"
          />
          <div className={styles.shareActions}>
            <IonButton
              fill="solid"
              className={copyFeedback ? styles.active : ''}
              onClick={() => {
                if (sharePopoverState.link?.id) {
                  handleCopyLink(sharePopoverState.link.id);
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IonIcon icon={copyFeedback ? checkmarkOutline : copyOutline} />
                <span>{copyFeedback ? 'Copied!' : 'Copy Link'}</span>
              </div>
            </IonButton>
            <IonButton
              fill="solid"
              onClick={() => {
                const link = sharePopoverState.link;
                if (link?.id && link?.name) {
                  handleShare(link.id, link.name);
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IonIcon icon={shareOutline} />
                <span>Share</span>
              </div>
            </IonButton>
            <IonButton
              fill="solid"
              onClick={() => {
                const link = sharePopoverState.link;
                if (link?.id) {
                  setQrPopoverState({ isOpen: true, link });
                  setSharePopoverState({ isOpen: false });
                  setTimeout(() => generateQRCode(link.id), 100);
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <IonIcon icon={qrCodeOutline} />
                <span>QR Code</span>
              </div>
            </IonButton>
          </div>
        </div>
      </IonPopover>

      <IonPopover
        isOpen={qrPopoverState.isOpen}
        onDidDismiss={() => setQrPopoverState({ isOpen: false })}
        className={styles.sharePopoverContainer}
        showBackdrop={true}
        backdropDismiss={true}
        mode="ios"
      >
        <div className={styles.qrPopover}>
          <div className={styles.modalHeader}>
            <h3>QR Code</h3>
            <IonButton
              fill="clear"
              className={styles.closeButton}
              onClick={() => setQrPopoverState({ isOpen: false })}
            >
              <IonIcon icon={closeOutline} />
            </IonButton>
          </div>
          <canvas id="qr-code"></canvas>
          <p>Scan to view content</p>
        </div>
      </IonPopover>
    </div>
  );
}; 